let currentUser = null;
const listeners = new Set();

function normalizeText(value) {
  return String(value ?? '').trim();
}

function normalizeCurrentUser(user) {
  if (!user || typeof user !== 'object') return null;
  const rawRoles = Array.isArray(user.roles) ? user.roles : [];
  const roles = rawRoles
    .map(role => ({
      ...role,
      rolle_id: role?.rolle_id ?? role?.id ?? null,
      rolle_code: normalizeText(role?.rolle_code ?? role?.roleCode),
      rolle_name: normalizeText(role?.rolle_name ?? role?.roleName),
      ist_primaer: role?.ist_primaer === true || role?.primary === true
    }))
    .filter(role => role.rolle_id || role.rolle_code || role.rolle_name);
  const primaryRole = user.primaryRole && typeof user.primaryRole === 'object'
    ? {
      ...user.primaryRole,
      rolle_id: user.primaryRole.rolle_id ?? user.primaryRole.id ?? null,
      rolle_code: normalizeText(user.primaryRole.rolle_code ?? user.primaryRole.roleCode),
      rolle_name: normalizeText(user.primaryRole.rolle_name ?? user.primaryRole.roleName),
      ist_primaer: true
    }
    : roles.find(role => role.ist_primaer) || roles[0] || null;
  const roleCodes = Array.isArray(user.roleCodes)
    ? user.roleCodes.map(normalizeText).filter(Boolean)
    : roles.map(role => role.rolle_code).filter(Boolean);

  return {
    ...user,
    benutzer_id: user.benutzer_id ?? user.id ?? null,
    vorname: normalizeText(user.vorname ?? user.firstName),
    nachname: normalizeText(user.nachname ?? user.lastName),
    firma: normalizeText(user.firma ?? user.company),
    rolle_id: user.rolle_id ?? user.role_id ?? primaryRole?.rolle_id ?? null,
    rolle_code: normalizeText(user.rolle_code ?? user.roleCode ?? primaryRole?.rolle_code),
    rolle_name: normalizeText(user.rolle_name ?? user.roleName ?? primaryRole?.rolle_name),
    roles,
    roleCodes,
    primaryRole,
    profilbild_url: normalizeText(user.profilbild_url ?? user.avatarUrl ?? user.profileImageUrl),
    active_flag: user.active_flag ?? true,
    permissions: user.permissions ?? null,
    effectivePermissions: user.effectivePermissions ?? null
  };
}

function notifyCurrentUserListeners() {
  listeners.forEach(listener => {
    try {
      listener(currentUser);
    } catch (error) {
      console.warn('Current user listener failed', error);
    }
  });
}

function getCurrentUser() {
  return currentUser;
}

function setCurrentUser(user) {
  currentUser = normalizeCurrentUser(user);
  notifyCurrentUserListeners();
  return currentUser;
}

function clearCurrentUser() {
  currentUser = null;
  notifyCurrentUserListeners();
}

function subscribeCurrentUser(listener) {
  if (typeof listener !== 'function') return () => undefined;
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function normalizePermissionCode(permissionCode) {
  return String(permissionCode ?? '').trim().toUpperCase();
}

function allowsPermissionEntry(entry) {
  if (entry === true || entry === 1) return true;
  if (entry === false || entry === 0 || entry == null) return false;
  if (typeof entry === 'string') {
    const normalized = entry.trim().toUpperCase();
    return ['1', 'TRUE', 'WAHR', 'J', 'JA', 'YES'].includes(normalized);
  }
  if (typeof entry !== 'object') return false;

  const value = entry.erlaubt_flag ?? entry.erlaubt ?? entry.allowed ?? entry.value ?? entry.enabled;
  return allowsPermissionEntry(value);
}

function permissionEntryCode(entry) {
  return normalizePermissionCode(
    entry?.berechtigung_code
    ?? entry?.permissionCode
    ?? entry?.permission_code
    ?? entry?.code
  );
}

function resolvePermission(collection, permissionCode) {
  const code = normalizePermissionCode(permissionCode);
  if (!code || !collection) return null;

  if (collection instanceof Map) {
    if (collection.has(code)) return allowsPermissionEntry(collection.get(code));
    const lowerCode = code.toLowerCase();
    if (collection.has(lowerCode)) return allowsPermissionEntry(collection.get(lowerCode));
    return null;
  }

  if (Array.isArray(collection)) {
    const match = collection.find(entry => (
      typeof entry === 'string'
        ? normalizePermissionCode(entry) === code
        : permissionEntryCode(entry) === code
    ));
    if (!match) return null;
    return typeof match === 'string' ? true : allowsPermissionEntry(match);
  }

  if (typeof collection === 'object') {
    const direct = collection[code] ?? collection[code.toLowerCase()];
    return direct === undefined ? null : allowsPermissionEntry(direct);
  }

  return null;
}

function hasPermission(permissionCode) {
  if (!currentUser || currentUser.active_flag === false) return false;

  const effective = resolvePermission(currentUser.effectivePermissions, permissionCode);
  if (effective !== null) return effective;

  const direct = resolvePermission(currentUser.permissions, permissionCode);
  return direct === true;
}

// Single source of truth for "is this user admin-tier" — an ADMINISTRATOR
// role, or either of the two permissions that grant admin-tier access
// (BENUTZER_VERWALTEN, ROLLEN_VERWALTEN). Previously duplicated independently
// across the admin user modal, the 3D alignment debug tool, and the app
// admin-center gate; centralized here so all three stay in sync.
function isAdminUser(user = getCurrentUser()) {
  if (!user || user.active_flag === false) return false;
  const roleCodes = new Set([
    user.rolle_code,
    ...(Array.isArray(user.roleCodes) ? user.roleCodes : []),
    ...(Array.isArray(user.roles) ? user.roles.map(role => role?.rolle_code) : [])
  ].filter(Boolean).map(code => String(code).trim().toUpperCase()));
  return roleCodes.has('ADMINISTRATOR')
    || hasPermission('BENUTZER_VERWALTEN')
    || hasPermission('ROLLEN_VERWALTEN');
}

export {
  getCurrentUser,
  setCurrentUser,
  clearCurrentUser,
  subscribeCurrentUser,
  hasPermission,
  isAdminUser
};
